'use client';

/**
 * Lightweight i18n — React Context + typed dictionaries.
 *
 * Why not `next-intl`: we don't need middleware routing, per-locale URLs,
 * pluralization rules, or date/currency libraries. Our copy is static UI
 * strings; a dict lookup is enough.
 *
 * Usage:
 *   const t = useT();
 *   t('nav.home')                      → "Home" (or "หน้าแรก" in Thai)
 *   t('home.category_prompts', { category: 'Writing' })
 *
 * Adding a new language:
 *   1. Create `src/locales/<code>.ts` exporting a DeepPartial of the EN shape.
 *   2. Register it in the `dictionaries` map below + add to `AVAILABLE_LOCALES`.
 *   3. That's it — missing keys fall back to English automatically.
 */

import { createContext, useCallback, useContext, useEffect, useMemo, useSyncExternalStore } from 'react';
import { en } from '@/locales/en';
import { th } from '@/locales/th';

export type Locale = 'en' | 'th';

export const AVAILABLE_LOCALES: { code: Locale; label: string; flag: string }[] = [
  { code: 'en', label: 'English',  flag: '🇬🇧' },
  { code: 'th', label: 'ไทย',      flag: '🇹🇭' },
];

const dictionaries: Record<Locale, unknown> = { en, th };

const STORAGE_KEY = 'locale';
const COOKIE_KEY = 'ps_locale';
const DEFAULT_LOCALE: Locale = 'en';

/** Read the locale cookie at module load. Used as the server snapshot
 * source so SSR + first client paint render the user's chosen language
 * immediately — no EN→TH "flicker" after hydration. */
function readCookieLocale(): Locale {
  if (typeof document === 'undefined') return DEFAULT_LOCALE;
  const m = document.cookie.match(new RegExp(`(?:^|; )${COOKIE_KEY}=([^;]+)`));
  if (m && (m[1] in dictionaries)) return m[1] as Locale;
  return DEFAULT_LOCALE;
}

type Ctx = {
  locale: Locale;
  setLocale: (l: Locale) => void;
  t: (key: string, vars?: Record<string, string | number>) => string;
};

const I18nContext = createContext<Ctx | null>(null);

/** Walk a nested dictionary with a dotted key. Returns undefined on miss. */
function lookup(dict: unknown, path: string): string | undefined {
  const parts = path.split('.');
  let cur: unknown = dict;
  for (const p of parts) {
    if (cur && typeof cur === 'object' && p in (cur as Record<string, unknown>)) {
      cur = (cur as Record<string, unknown>)[p];
    } else {
      return undefined;
    }
  }
  return typeof cur === 'string' ? cur : undefined;
}

/** Replace `{name}` placeholders with values from `vars`. */
function interpolate(template: string, vars?: Record<string, string | number>): string {
  if (!vars) return template;
  return template.replace(/\{(\w+)\}/g, (match, name) =>
    name in vars ? String(vars[name]) : match
  );
}

// localStorage-backed external store for the current locale. We use
// `useSyncExternalStore` instead of `useState + useEffect(localStorage)` so
// the initial client render matches the SSR HTML *and* React still re-paints
// once with the persisted value as soon as hydration completes — without a
// hydration-mismatch error in React 19 / Next 16.
const LOCALE_EVENT = 'ps-locale-change';

function readLocale(): Locale {
  // localStorage is the source of truth on the client; the cookie just
  // mirrors it so the server can render the right language on first paint.
  try {
    const saved = localStorage.getItem(STORAGE_KEY) as Locale | null;
    if (saved && saved in dictionaries) return saved;
  } catch { /* ignore */ }
  // Fall back to the cookie if localStorage is empty (first visit after
  // the user picked a language on a different device but with the cookie
  // already set — defensive, rarely triggers).
  return readCookieLocale();
}

function subscribeLocale(cb: () => void): () => void {
  if (typeof window === 'undefined') return () => {};
  // Cross-tab updates fire `storage`; same-tab updates fire our custom event
  // (since `storage` only fires for *other* tabs).
  const onStorage = (e: StorageEvent) => { if (e.key === STORAGE_KEY) cb(); };
  window.addEventListener('storage', onStorage);
  window.addEventListener(LOCALE_EVENT, cb);
  return () => {
    window.removeEventListener('storage', onStorage);
    window.removeEventListener(LOCALE_EVENT, cb);
  };
}

export function I18nProvider({
  children,
  initialLocale,
}: {
  children: React.ReactNode;
  /** Locale resolved server-side from the `ps_locale` cookie. When provided,
   * SSR + first client paint render this language — eliminating the
   * post-hydration EN→TH flash. Defaults to DEFAULT_LOCALE for backward-compat. */
  initialLocale?: Locale;
}) {
  const ssrLocale = initialLocale ?? DEFAULT_LOCALE;
  const locale = useSyncExternalStore(
    subscribeLocale,
    readLocale,         // client snapshot — reads localStorage / cookie
    () => ssrLocale,    // server snapshot — uses cookie value passed by RootLayout
  );

  const setLocale = useCallback((l: Locale) => {
    try { localStorage.setItem(STORAGE_KEY, l); } catch { /* ignore */ }
    // Mirror to a cookie so the server can pick it up on the NEXT request.
    // Path=/ + 1-year max-age + SameSite=Lax (so it rides on top-level nav).
    if (typeof document !== 'undefined') {
      document.cookie = `${COOKIE_KEY}=${l}; path=/; max-age=${60 * 60 * 24 * 365}; samesite=lax`;
    }
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new Event(LOCALE_EVENT));
    }
    if (typeof document !== 'undefined') {
      document.documentElement.lang = l;
    }
  }, []);

  // Keep <html lang> in sync with the resolved locale after hydration.
  useEffect(() => {
    if (typeof document !== 'undefined') document.documentElement.lang = locale;
  }, [locale]);

  const t = useCallback(
    (key: string, vars?: Record<string, string | number>): string => {
      const primary = lookup(dictionaries[locale], key);
      const fallback = lookup(dictionaries[DEFAULT_LOCALE], key);
      const value = primary ?? fallback ?? key;
      return interpolate(value, vars);
    },
    [locale]
  );

  const value = useMemo(() => ({ locale, setLocale, t }), [locale, setLocale, t]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): Ctx {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error('useI18n must be used inside <I18nProvider>');
  return ctx;
}

/** Short convenience hook for the common case of just reading strings. */
export function useT() {
  return useI18n().t;
}
